<template>
    <ActivityDetailsCard
        activityType="Premium Research"
        :title="report.title"
        :entity="item.entity"
        class="loading-container"
    >
        <template #sub-header>
            <div class="cell">
                <h5>Published</h5>
                {{ date }}
            </div>
            <div class="cell">
                <h5>Evaluation</h5>
                {{ report.evaluation }}
            </div>
        </template>

        <section>
            <div class="actions">
                <download-menu v-if="canDownload" button-class="primary" :report="report" text="Download PDF" />
                <router-link
                    class="primary button"
                    :to="{ name: 'executive-page', params: { id: item.entity.id }, query: { tab: 'Premium Research' } }"
                    v-if="canDownload"
                >
                    View Report
                </router-link>
                <div class="sales-contact" v-else>
                    <button class="primary" type="button" @click="requestSalesContact">
                        <span>Have Sales Contact Me</span>
                    </button>
                    <div class="contact">
                        <span>Sales will contact you with the email on file.</span>
                        <br />
                        <a class="primary-link" href="mailto:sales@paragonintel.com">
                            Alternatively, email our sales team
                        </a>
                    </div>
                </div>
            </div>
        </section>

        <profile-section :title="report.title">
            <p v-html="report.overview"></p>
        </profile-section>

        <InterviewTiles :interviews="interviews" @open="openInterview" />

        <ActivityStockPriceLine :item="item" />

        <loading :active="updating" :is-full-page="false" />

        <InterviewModal :interview="interview" tab="Transcript" @dismiss="interview = null" v-if="interview" />
    </ActivityDetailsCard>
</template>

<script setup>
import { CeoChangeReportService } from '@/services/ceoChangeReportService'
import { InterviewService } from '@/services/interviewService'

import ActivityDetailsCard from '../ActivityDetailsCard.vue'
import ActivityStockPriceLine from '../ActivityStockPriceLine.vue'
import DownloadMenu from '@/components/ceo-change/download-menu'
import InterviewTiles from '@/components/research/InterviewTiles.vue'
import ProfileSection from '@/components/shared/profile-section'
import SalesService from '@/services/salesService'

import { asyncComputed } from '@/utils/asyncComputed'
import { computed, inject, ref } from 'vue'
import InterviewModal from '@/components/executives/Research/InterviewModal.vue'

const props = defineProps({
    item: {
        type: Object,
        required: true,
    },
})

const canDownload = computed(() => report.value.accessLevel !== null)

const date = computed(() => new Date(report.value.publishedDate).toLocaleDateString())

const updating = computed(() => report.updating)
const reportId = computed(() => props.item.details?.entityId ?? props.item.details)

const interviews = asyncComputed(async () => {
    if (reportId.value) {
        return await InterviewService.listByReportId(reportId.value)
    }

    return []
}, [])

const report = asyncComputed(() => CeoChangeReportService.get(reportId.value), {
    accessLevel: null,
    company: {
        id: 0,
        name: '',
        symbol: '',
    },
    executive: {
        id: 0,
        colloquialName: '',
    },
    title: '',
})

const interview = ref(null)

function openInterview(i) {
    interview.value = i
}

const flash = inject('flash')

async function requestSalesContact() {
    try {
        await SalesService.requestContactCeoChange()
        flash('Thanks for your interest in Premium Research. A member of our sales team will be in touch.', 'success')
    } catch {
        flash('An issue occurred. Please try again later.', 'error')
    }
}
</script>

<style scoped>
.actions {
    display: flex;
    flex-direction: row;
    gap: 16px;
    margin-bottom: 32px;
    justify-content: center;
}

.actions > * {
    flex: 1 1 50%;
}

.sales-contact {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.interview-tiles {
    margin-bottom: 24px;
}
</style>
